import type { AudioCodec, VideoCodec } from 'mediabunny';
import { clipLength, clipStarts, isEmptySlot, totalLength, voiceState, type VideoProject } from './project';

/*
 * Settings and plain calculations for making copies and finished videos. No Mediabunny code here (only its types),
 * so the screens load fast; src/video/convert.ts and src/video/exporter.ts (which carry Mediabunny) are loaded
 * the first time a clip is read or a video is written (src/video/engine.ts).
 */

/** the longest side of a copy and its shortest side (a 4K phone video becomes 1080 × 1920) */
export const COPY_LONG = 1920, COPY_SHORT = 1080;
/** same settings as the iPhone test (10 s of 4K → 7.0 MB) */
export const COPY_BITRATE = 6_000_000, AUDIO_BITRATE = 128_000, AUDIO_RATE = 48_000;
/** measured on an iPhone (18.7): a 4K clip is shrunk at 0.63 × real time, so waiting ≈ 1.6 × the part's length */
export const PREP_SECONDS_PER_SECOND = 1.6;
/** a copy takes about this many bytes per second (6 Mbps video + 128 kbps sound) */
export const COPY_BYTES_PER_SECOND = (COPY_BITRATE + AUDIO_BITRATE) / 8;

/**
 * Codecs for copies and finished videos: H.264 + AAC (plays everywhere, what social apps expect).
 * The browser test (tests/e2e) runs in a Chromium without H.264, so it may switch to VP9 + Opus through this hook.
 */
declare global { interface Window { __craftVideoCodecs?: { video: VideoCodec; audio: AudioCodec } } }
export const codecs = (): { video: VideoCodec; audio: AudioCodec } => window.__craftVideoCodecs ?? { video: 'avc', audio: 'aac' };

/** size of a copy: same shape as the original, at most 1080 × 1920 (or 1920 × 1080), even numbers */
export function copySize(w: number, h: number): { width: number; height: number } {
  const long = Math.max(w, h), short = Math.min(w, h);
  const s = Math.min(1, COPY_LONG / long, COPY_SHORT / short);
  const even = (x: number) => Math.max(2, Math.round(x * s / 2) * 2);
  return { width: even(w), height: even(h) };
}

export const FPS = 30;
export const EXPORT_BITRATE = 6_000_000;
/** music longer than the video fades out over the last seconds */
export const MUSIC_FADE_SECONDS = 2;
/** measured on an iPhone (18.7): 3.4 × real time */
export const EXPORT_SPEED = 3.4;
export const exportBytesPerSecond = () => (EXPORT_BITRATE + AUDIO_BITRATE) / 8;

/** frame range of each clip, rounded once so the clips together have exactly round(total × FPS) frames */
export function frameRanges(p: VideoProject): Array<{ first: number; count: number }> {
  const starts = clipStarts(p);
  return p.clips.map((c, k) => {
    const first = Math.round(starts[k] * FPS);
    const next = Math.round((starts[k] + clipLength(c)) * FPS);
    return { first, count: Math.max(0, next - first) };
  });
}

/** Things to tell the person before exporting (src/ui/videoView.ts shows them). */
export interface ExportNote { level: 'ok' | 'warn' | 'stop'; text: string }
export function exportNotes(p: VideoProject, missing: Set<string>): ExportNote[] {
  const notes: ExportNote[] = [];
  const total = totalLength(p);
  if (!p.clips.length) { notes.push({ level: 'stop', text: 'ยังไม่มีคลิป เพิ่มคลิปก่อนส่งออก' }); return notes; }
  // เทมเพลตวิดีโอ: every slot needs its clip or picture
  const empty = p.clips.map((c, k) => ({ c, k })).filter(x => isEmptySlot(x.c));
  if (empty.length) notes.push({ level: 'stop', text: `ยังมีช่องว่าง ${empty.length} ช่อง (${empty.map(x => `${x.k + 1}. ${x.c.slot?.label ?? ''}`).join(', ')}) กด "ช่องคลิป" เพื่อเติม` });
  // the hold a voice needs is not a "clip too short" warning: the scene was lengthened for the voice on purpose
  const held = p.clips.map((c, k) => ({ c, k })).filter(x => !isEmptySlot(x.c) && x.c.kind === 'video' && x.c.hold - (x.c.voice?.extra ?? 0) > 0.05);
  if (held.length) notes.push({ level: 'warn', text: `คลิปสั้นกว่าช่อง ภาพสุดท้ายจะค้าง: ${held.map(x => `ช่องที่ ${x.k + 1} ค้าง ${x.c.hold.toFixed(1)} วิ`).join(', ')}` });
  const gone = p.clips.filter(c => !isEmptySlot(c) && missing.has(c.media)).length;
  if (gone) notes.push({ level: 'stop', text: `${gone} คลิปไม่อยู่ในเครื่องนี้ ใส่คลิปกลับก่อนส่งออก (แถบแดงด้านบน)` });
  else if (!empty.length) notes.push({ level: 'ok', text: `คลิปครบ ${p.clips.length} คลิป อยู่ในเครื่องนี้` });
  if (p.music) {
    if (missing.has(p.music.media)) notes.push({ level: 'stop', text: `เพลง "${p.music.name}" ไม่อยู่ในเครื่องนี้ เลือกไฟล์เพลงใหม่ หรือเอาเพลงออก` });
    else if (p.music.dur > total + 0.05) notes.push({ level: 'warn', text: `เพลงยาวกว่าวิดีโอ ${Math.round(p.music.dur - total)} วิ จะตัดท้ายให้ และค่อย ๆ เบาลงใน ${MUSIC_FADE_SECONDS} วิสุดท้าย` });
    else if (p.music.dur < total - 0.05) notes.push({ level: 'warn', text: `เพลงสั้นกว่าวิดีโอ ${Math.round(total - p.music.dur)} วิ ช่วงท้ายจะไม่มีเพลง` });
  }
  // เสียงพากย์ AI (v4): words with no voice, or a voice made from other words, never go out silently
  const states = p.clips.map((c, k) => ({ k, s: voiceState(p, c) }));
  const noVoice = states.filter(x => x.s === 'missing').map(x => x.k + 1);
  const stale = states.filter(x => x.s === 'stale').map(x => x.k + 1);
  if (noVoice.length) notes.push({ level: 'stop', text: `ฉากที่ ${noVoice.join(', ')} มีข้อความพากย์แต่ยังไม่ได้สร้างเสียง กด "พากย์" → สร้างเสียง (หรือลบข้อความออก)` });
  if (stale.length) notes.push({ level: 'stop', text: `ฉากที่ ${stale.join(', ')} แก้ข้อความหรือเปลี่ยนเสียงหลังสร้างเสียงแล้ว เสียงไม่ตรงกับข้อความ กด "พากย์" → สร้างเสียงใหม่` });
  const ready = states.filter(x => x.s === 'ready').length;
  if (ready && !noVoice.length && !stale.length) notes.push({ level: 'ok', text: `เสียงพากย์ ${ready} ฉาก${p.music || p.clips.some(c => c.hasAudio) ? ' (เพลงและเสียงคลิปเบาลงตอนพากย์)' : ''}` });
  const silent = !ready && p.clips.every(c => !c.hasAudio || c.volume === 0) && (!p.music || p.music.volume === 0);
  if (silent) notes.push({ level: 'warn', text: 'วิดีโอนี้จะไม่มีเสียง (ทุกคลิปปิดเสียงหรือไม่มีเสียง และไม่มีเพลง)' });
  return notes;
}

